import { getKidTemplateTypeRank } from '../../../../../../api/kids/types';
import { mainColors } from '../../../../../../styling/theme';
import type {
  KidTemplateModel,
  KidTemplateOutputModel,
  KidTemplateType,
} from '../../../../../../api/kids/types';
import type {
  FormatOptions,
  PdfElement,
} from '../../types/KidPdfGeneratorV2.types';

export type SelectableKidTemplate = Pick<
  KidTemplateModel,
  | 'template_id'
  | 'template_name'
  | 'template_type'
  | 'template_content'
  | 'reference_id'
  | 'format_options'
> &
  Partial<Pick<KidTemplateOutputModel, 'rendered_content'>>;

export const getUniqueNecessaryTemplates = (
  templateGroups?: Record<string, KidTemplateModel[]>,
): SelectableKidTemplate[] => {
  if (!templateGroups) {
    return [];
  }

  const seenTemplateIds = new Set<string>();
  const uniqueTemplates: SelectableKidTemplate[] = [];

  Object.values(templateGroups)
    .flat()
    .forEach((template) => {
      if (seenTemplateIds.has(template.template_id)) {
        return;
      }

      seenTemplateIds.add(template.template_id);
      uniqueTemplates.push(template);
    });

  return uniqueTemplates;
};

export const getSelectedTemplates = (
  selectedTemplateIds: string[] | undefined,
  availableTemplates: SelectableKidTemplate[],
): SelectableKidTemplate[] => {
  if (!selectedTemplateIds?.length) {
    return [];
  }

  const templateLookup = new Map(
    availableTemplates.map((template) => [template.template_id, template]),
  );

  return selectedTemplateIds
    .map((templateId) => templateLookup.get(templateId))
    .filter((template): template is SelectableKidTemplate => Boolean(template));
};

export const sortClientTemplates = (
  templates: SelectableKidTemplate[],
): SelectableKidTemplate[] =>
  [...templates].sort((left, right) => {
    const rankDiff =
      getKidTemplateTypeRank(right.template_type as KidTemplateType) -
      getKidTemplateTypeRank(left.template_type as KidTemplateType);

    if (rankDiff !== 0) {
      return rankDiff;
    }

    return left.template_name.localeCompare(right.template_name);
  });

export const prettifyTemplateGroupLabel = (groupKey: string): string =>
  groupKey
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const buildTemplatePdfElements = (
  templates: SelectableKidTemplate[],
  existingTemplateIds: Iterable<string> = [],
): PdfElement[] => {
  const knownTemplateIds = new Set(
    Array.from(existingTemplateIds).filter(Boolean),
  );

  return templates
    .filter((template) => !knownTemplateIds.has(template.template_id))
    .map((template) => {
      const formatOptions = template.format_options as FormatOptions | undefined;
      const layout = formatOptions?.layout;
      let cleanedFormatOptions: PdfElement['format_options'] = undefined;

      if (formatOptions) {
        if (layout) {
          const { layout: _layout, ...rest } = formatOptions;
          cleanedFormatOptions =
            Object.keys(rest).length > 0
              ? (rest as PdfElement['format_options'])
              : undefined;
        } else {
          cleanedFormatOptions = formatOptions as PdfElement['format_options'];
        }
      }

      return {
        fieldId: `${template.template_id}`,
        tag: 'text',
        content: template.rendered_content || template.template_content,
        ...(cleanedFormatOptions ? { format_options: cleanedFormatOptions } : {}),
        ...(layout ? { layout } : {}),
        template_id: template.template_id,
        template_type: template.template_type,
        rendered_content: template.rendered_content || template.template_content,
      };
    });
};

export const TEMPLATE_BACKGROUND_BY_TYPE: Record<KidTemplateType, string> = {
  manco: mainColors.pastelGreen_lighter,
  administrator: mainColors.pastelCoral,
  ciu_fund: mainColors.pastelPurple_lighter,
  fund_manager: mainColors.pastelYellow_lighter,
  sub_fund: mainColors.pastelBlue_lighter,
};

export const TEMPLATE_BORDER_BY_TYPE: Record<KidTemplateType, string> = {
  manco: mainColors.tertiaryGreen,
  administrator: mainColors.sternRed,
  ciu_fund: mainColors.fourthPurple,
  fund_manager: mainColors.accentOrange,
  sub_fund: mainColors.sectionHeader,
};
